/**
 * 「這是您嗎？」確認卡的門牌號比對工具。
 *
 * 背景：確認卡原本只把後端回的遮罩地址（例：`台中市西屯區＊＊路 20 號`）印出來讓客戶自行判斷。
 * 這樣給客戶的辨識資訊太少（路名被遮掉，只剩行政區），而且完全不構成驗證——任何人看到卡片
 * 都能按「這是我」。改為 **露路名、遮門牌號**，並由客戶輸入門牌號送後端 verifyClaimedCis 驗證。
 *
 * 遮罩本體由後端 `address_masked` 產生（lpg-app-api，現已是「露路名、遮門牌號」）。前端仍一律再跑
 * 一次 {@link maskHouseNo}：對新格式是 no-op，但若某條路徑回到舊格式（遮路名、露門牌號），這一層
 * 會把答案從畫面上拿掉。答案不能印在要客戶回答的卡片上，這道保險不移除。
 *
 * 曾有 `HOUSE_NO_VERIFY_ON`（`REACT_APP_HOUSE_NO_VERIFY`）旗標，用途是讓前端可以先合進 dev、
 * 等後端 `verifyClaimedCis` 收 `house_no` 之後再生效。後端已就緒，旗標任務結束並移除。
 */

/** 全形數字 → 半形。後端遮罩字串與客戶輸入都可能夾雜全形。 */
const toHalfWidthDigits = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

/** 門牌號本體：數字（可含 `-` / `之` 連接的支號），後面接「號」。不含巷、弄、樓、室。 */
const HOUSE_NO_RE = /[0-9０-９]+(?:\s*[-－之]\s*[0-9０-９]+)*\s*號/g;

/**
 * 把地址字串裡的門牌號遮成 `＊＊號`（巷／弄／樓／室的數字保留，那些不是客戶要輸入的欄位）。
 * 沒有「號」的地址（後端已遮或本來就殘缺）原樣回傳。
 */
export const maskHouseNo = (address?: string | null): string => (address || "").replace(HOUSE_NO_RE, "＊＊號");

/**
 * 客戶輸入的門牌號正規化：去空白、全形轉半形、拿掉「號」、`之`／全形減號統一成 `-`。
 * 例：`２０ 之 1 號` → `20-1`。送後端驗證前一律走這支，避免同一戶因寫法不同驗不過。
 */
export const normalizeHouseNo = (input?: string | null): string =>
  toHalfWidthDigits(input || "")
    .replace(/\s+/g, "")
    .replace(/號$/, "")
    .replace(/[之－]/g, "-");

/** 正規化後是否像個門牌號：數字開頭，可接一段 `-支號`。純為前端擋下明顯打錯，最終仍以後端驗證為準。 */
export const isValidHouseNo = (input?: string | null): boolean => /^\d{1,6}(-\d{1,4})?$/.test(normalizeHouseNo(input));
